import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline, MaskReveal } from "@/components/Motion";
import { LocationsMap } from "@/components/LocationsMap";
import { WhoWeAre } from "@/components/WhoWeAre";
import { SEO } from "@/components/SEO";
import { TrustedExperience } from "@/components/TrustedExperience";
import { MarbleBreak } from "@/components/MarbleBreak";
import { ASSETS } from "@/lib/assets";

const CHAPTERS = [
  { n: "01", title: "Design", body: "Every project begins with an idea and a sketch. We translate your brand and objectives into architectural exhibition environments — concept, spatial planning and photoreal visualisation — before a single panel is built." },
  { n: "02", title: "Build", body: "Italian craftsmanship sits at the heart of our build. Precision fabrication, premium materials and meticulous finishing produce stands that feel considered from the first glance to the closest detail." },
  { n: "03", title: "Deliver", body: "We manage logistics, installation and on-site support so your team simply arrives to a finished stand — delivered on time, on brief and on budget." },
];

const About = () => (
  <div data-testid="about-page">
    <SEO title="About EVO Exhibitions — Italian Craftsmanship, Middle East Expertise" description="Inspired by 'Evoluzione' and rooted in Sicily, EVO Exhibitions blends Italian design with Middle East exhibition expertise. Headquarters in Italy, office in Dubai." path="/about" />
    {/* Hero */}
    <section className="px-6 md:px-12 lg:px-20 pt-40 md:pt-48 pb-16">
      <Reveal><Overline className="block mb-8">About EVO</Overline></Reveal>
      <h1 className="font-serif font-light text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1] text-evo-pearl max-w-5xl">
        <MaskReveal delay={0.1}>Inspired by the Italian</MaskReveal>
        <MaskReveal delay={0.25}>word <span className="text-evo-gold italic">Evoluzione</span>.</MaskReveal>
      </h1>
    </section>

    {/* Origin story */}
    <section className="px-6 md:px-12 lg:px-20 pb-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <p className="font-serif font-light text-2xl md:text-3xl leading-[1.3] text-evo-pearl mb-8">
            Rooted in <span className="text-evo-gold italic">Sicily, Italy</span> — a heritage of passion, craftsmanship and timeless design.
          </p>
          <p className="text-evo-ash font-light leading-relaxed max-w-lg mb-6">
            EVO was born from the Italian word <em className="text-evo-pearl/90 not-italic">Evoluzione</em> — evolution. It captures our belief that great exhibition design never stands still: it evolves with every brand, every audience and every show floor.
          </p>
          <p className="text-evo-ash font-light leading-relaxed max-w-lg">
            We combine Italian vision with Middle East exhibition expertise — headquartered in Italy with an office in Dubai — to design, build and deliver stands that carry a sense of craft and character wherever they stand.
          </p>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-evo-gold">Design. Build. Deliver.</p>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative aspect-[4/5] overflow-hidden group">
            <img src={ASSETS.curvedMarble} alt="EVO Italian architectural craftsmanship" className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
            <div className="absolute inset-0 border border-white/10" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* Who We Are collage */}
    <WhoWeAre />

    {/* Premium marble break */}
    <MarbleBreak />

    {/* Trusted Experience */}
    <TrustedExperience />

    {/* Manifesto chapters */}
    <section className="px-6 md:px-12 lg:px-20 py-24 border-y border-white/5 bg-evo-marble1/70">
      <Reveal><Overline className="block mb-16">How we work</Overline></Reveal>
      <div className="space-y-0">
        {CHAPTERS.map((c) => (
          <Reveal key={c.n} y={30}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-white/10 group">
              <div className="md:col-span-3">
                <span className="font-serif text-6xl md:text-7xl text-evo-gold/80 leading-none">{c.n}</span>
              </div>
              <h2 className="md:col-span-3 font-serif text-3xl md:text-4xl text-evo-pearl group-hover:text-evo-gold transition-colors duration-500">{c.title}</h2>
              <p className="md:col-span-6 text-evo-ash font-light leading-relaxed text-base md:text-lg">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Locations */}
    <LocationsMap />

    {/* Closing band (marble, not a map) */}
    <section className="relative overflow-hidden">
      <img src={ASSETS.marble} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-evo-black/75" />
      <div className="relative z-10 px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <Reveal>
          <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight text-evo-pearl max-w-3xl text-balance">
            Italian craftsmanship. <span className="text-evo-gold italic">Regional expertise.</span> Global impact.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/contact" data-testid="about-cta-btn" className="mt-12 inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-9 py-5 text-xs uppercase tracking-[0.2em] group">
            Work With Us
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default About;
